import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { askQuestion, runQuery } from '../services/api.js';
import { getStoredSessionId, storeSessionId, getStoredSessions, storeSessions, createLocalSessionId } from '../utils/sessionManager.js';
import { useAuth } from '../contexts/AuthContext.jsx';

// Context for shared conversation state (consumed by useConversation)
const ConversationContext = createContext(null);

/**
 * Stateful hook that holds conversation state. Used only inside ConversationProvider.
 * Sessions persist in localStorage per user; a blank thread is created only when
 * the user has no threads for the current company.
 * @returns {Object} Conversation state and methods
 */
function useConversationState() {
  const { activeCompany, user } = useAuth();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessions, setSessions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const hasHydratedRef = useRef(false);

  // Ref for stale-response guard: current company/session when response is applied
  const currentContextRef = useRef({
    activeCompanyId: activeCompany?.id,
    currentSessionId: null
  });
  useEffect(() => {
    currentContextRef.current = {
      activeCompanyId: activeCompany?.id,
      currentSessionId
    };
  }, [activeCompany?.id, currentSessionId]);

  // Clear loading state when user switches company or session so the new view isn't blocked
  useEffect(() => {
    setIsLoading(false);
  }, [activeCompany?.id, currentSessionId]);

  // Hydrate from per-user storage; restores currentSessionId from storage or first session for company
  useEffect(() => {
    if (user && activeCompany?.id) {
      const storedSessions = getStoredSessions(user.id);
      const storedSessionId = getStoredSessionId(user.id);

      const validSessions = {};
      const oldToNew = {};
      Object.entries(storedSessions).forEach(([key, session]) => {
        if (!session?.company_id) return;
        const { backendSessionId, ...rest } = session;
        const id = backendSessionId || session.id || key;
        oldToNew[key] = id;
        if (session.id) oldToNew[session.id] = id;
        validSessions[id] = { ...rest, id };
      });
      const companySessions = Object.values(validSessions)
        .filter((s) => s.company_id === activeCompany.id)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      let newCurrentId = null;
      const resolvedStoredId = oldToNew[storedSessionId] || storedSessionId;
      const storedSession = validSessions[resolvedStoredId];
      if (storedSession && storedSession.company_id === activeCompany.id) {
        newCurrentId = resolvedStoredId;
      } else if (companySessions.length > 0) {
        newCurrentId = companySessions[0].id;
      }

      setSessions(validSessions);
      setCurrentSessionId(newCurrentId);
      if (newCurrentId) {
        storeSessionId(newCurrentId, user.id);
      }
      storeSessions(validSessions, user.id);
      hasHydratedRef.current = true;
      setSessionsLoaded(true);
    } else if (!user) {
      setSessions({});
      setCurrentSessionId(null);
      setError(null);
      hasHydratedRef.current = false;
      setSessionsLoaded(false);
    }
  }, [user, activeCompany?.id]);

  // Ensure one blank session when user has no threads for the current company.
  // Skip while an inbound ?session_id= is still being applied.
  useEffect(() => {
    if (!sessionsLoaded || currentSessionId !== null || !activeCompany?.id || !user?.id) return;
    if (searchParams.get('session_id')) return;
    const companySessions = Object.values(sessions).filter(
      (s) => s.company_id === activeCompany.id
    );
    if (companySessions.length > 0) return;
    const newSessionId = createLocalSessionId();
    const newSession = {
      id: newSessionId,
      company_id: activeCompany.id,
      messages: [],
      createdAt: new Date().toISOString(),
      messageCount: 0
    };
    setSessions((prev) => {
      const updated = { ...prev, [newSessionId]: newSession };
      storeSessions(updated, user.id);
      return updated;
    });
    setCurrentSessionId(newSessionId);
    storeSessionId(newSessionId, user.id);
  }, [sessionsLoaded, currentSessionId, activeCompany?.id, user?.id, sessions, searchParams]);

  const createNewSession = useCallback(
    (options = {}) => {
      const opts = typeof options === 'string' ? { sessionId: options } : options;
      const { sessionId = null, title = null } = opts;

      const newSessionId = sessionId || createLocalSessionId();
      const newSession = {
        id: newSessionId,
        company_id: activeCompany?.id,
        messages: [],
        createdAt: new Date().toISOString(),
        messageCount: 0,
        ...(title && { title })
      };

      setSessions((prev) => {
        const updated = { ...prev, [newSessionId]: newSession };
        storeSessions(updated, user?.id);
        return updated;
      });

      return newSessionId;
    },
    [activeCompany?.id, user?.id]
  );

  const updateSessionId = useCallback(
    (newSessionId) => {
      setCurrentSessionId(newSessionId);
      storeSessionId(newSessionId, user?.id);
    },
    [user?.id]
  );

  const addMessage = useCallback(
    (role, content, metadata = {}, options = {}) => {
      if (!currentSessionId) {
        console.warn('Cannot add message: no current session');
        return;
      }

      const newMessage = {
        id: Date.now(),
        role,
        content,
        timestamp: new Date().toISOString(),
        metadata,
        ...(options.isHidden && { isHidden: true }),
        ...(options.hideGraph && { hideGraph: true })
      };

      setSessions((prev) => {
        const updated = {
          ...prev,
          [currentSessionId]: {
            ...prev[currentSessionId],
            messages: [...(prev[currentSessionId]?.messages || []), newMessage],
            messageCount: (prev[currentSessionId]?.messageCount || 0) + 1
          }
        };
        storeSessions(updated, user?.id);
        return updated;
      });
    },
    [currentSessionId, user?.id]
  );

  const addMessageToSession = useCallback(
    (sessionId, role, content, metadata = {}, options = {}) => {
      if (!sessionId || !user?.id) return;
      const newMessage = {
        id: Date.now(),
        role,
        content,
        timestamp: new Date().toISOString(),
        metadata,
        ...(options.isHidden && { isHidden: true }),
        ...(options.hideGraph && { hideGraph: true })
      };
      setSessions((prev) => {
        const session = prev[sessionId];
        if (!session) return prev;
        const updated = {
          ...prev,
          [sessionId]: {
            ...session,
            messages: [...(session.messages || []), newMessage],
            messageCount: (session.messageCount || 0) + 1
          }
        };
        storeSessions(updated, user.id);
        return updated;
      });
    },
    [user?.id]
  );

  const sendQuestion = useCallback(async (query, options = {}) => {
    if (!query.trim()) return;

    if (!currentSessionId) {
      console.warn('Cannot send question: no current session');
      return;
    }

    if (!activeCompany?.id) {
      setError(t('errors.noActiveCompany'));
      addMessage('assistant', t('errors.noCompanySelected'), {
        error: true
      });
      return;
    }

    const companyIdForRequest = activeCompany.id;
    const sessionIdForRequest = currentSessionId;
    const inputParameters = options?.input_parameters;

    try {
      setIsLoading(true);
      setError(null);
      setSelectedMessageId(null);

      const existing = sessions[sessionIdForRequest];
      const hasBackendHistory = Boolean(existing?.fromBackend) || (existing?.messages?.length || 0) > 0;
      const sessionIdToSend = hasBackendHistory ? sessionIdForRequest : null;

      addMessage('user', query);
      const response = await askQuestion(query, sessionIdToSend, activeCompany.id, inputParameters);
      const assignedSessionId = response.session_id || sessionIdForRequest;

      let responseContent = response.response || response.message || t('errors.noResponseContent');
      const sqlErrorPattern = /Sorry, Revetall can not answer that question yet/i;
      if (sqlErrorPattern.test(responseContent)) {
        responseContent = t('errors.sqlProcessingError');
      }

      const isStillCurrent =
        currentContextRef.current.activeCompanyId === companyIdForRequest &&
        (currentContextRef.current.currentSessionId === sessionIdForRequest ||
          currentContextRef.current.currentSessionId === assignedSessionId);

      const assistantMessage = {
        id: Date.now(),
        role: 'assistant',
        content: responseContent,
        timestamp: new Date().toISOString(),
        metadata: {
          data: response.data,
          sessionId: response.session_id,
          queryId: response.query_id,
          followUpIntro: response.follow_up_intro ?? '',
          followUpQuestions: response.follow_up_questions ?? [],
          clarification: response.clarification ?? null,
        }
      };

      setSessions((prev) => {
        const source = prev[sessionIdForRequest] || prev[assignedSessionId];
        if (!source) return prev;
        const next = { ...prev };
        const targetId = assignedSessionId;
        if (targetId !== sessionIdForRequest && next[sessionIdForRequest] && !next[targetId]) {
          delete next[sessionIdForRequest];
        }
        const session = next[targetId] || source;
        next[targetId] = {
          ...session,
          id: targetId,
          fromBackend: true,
          ...(response.title && { title: response.title }),
          messages: [...(session.messages || []), assistantMessage],
          messageCount: (session.messageCount || 0) + 1
        };
        storeSessions(next, user.id);
        return next;
      });
      if (isStillCurrent && assignedSessionId !== sessionIdForRequest) {
        setCurrentSessionId(assignedSessionId);
        storeSessionId(assignedSessionId, user.id);
      }
    } catch (err) {
      console.error('Failed to send question:', err);

      let userErrorMessage;
      let systemErrorMessage = err.message || t('errors.failedToGetResponse');
      if (err.message?.includes("don't have access")) {
        userErrorMessage = err.message;
        systemErrorMessage = t('errors.accessDenied');
      } else if (err.message?.includes('Authentication failed')) {
        userErrorMessage = t('errors.sessionExpired');
      } else {
        userErrorMessage = t('errors.genericError');
      }

      const isStillCurrent =
        currentContextRef.current.activeCompanyId === companyIdForRequest &&
        currentContextRef.current.currentSessionId === sessionIdForRequest;

      if (isStillCurrent) {
        setError(systemErrorMessage);
        addMessage('assistant', userErrorMessage, { error: true });
      } else {
        addMessageToSession(sessionIdForRequest, 'assistant', userErrorMessage, { error: true });
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentSessionId, sessions, addMessage, addMessageToSession, activeCompany, user?.id, t]);

  const deleteSession = useCallback((sessionIdToDelete) => {
    setSessions(prev => {
      const newSessions = { ...prev };
      delete newSessions[sessionIdToDelete];

      if (sessionIdToDelete === currentSessionId) {
        const remainingSessions = Object.values(newSessions)
          .filter(s => s.company_id === activeCompany?.id)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        if (remainingSessions.length > 0) {
          const newCurrentId = remainingSessions[0].id;
          setCurrentSessionId(newCurrentId);
          updateSessionId(newCurrentId);
        } else {
          const newId = createNewSession();
          setCurrentSessionId(newId);
          updateSessionId(newId);
        }
      }

      storeSessions(newSessions, user?.id);
      return newSessions;
    });
    setError(null);
  }, [currentSessionId, updateSessionId, createNewSession, activeCompany?.id, user?.id]);

  const startNewSession = useCallback((options = {}) => {
    const newSessionId = createNewSession(options);
    setCurrentSessionId(newSessionId);
    updateSessionId(newSessionId);
    setError(null);
    return newSessionId;
  }, [createNewSession, updateSessionId]);

  const switchSession = useCallback((sessionId) => {
    if (sessions[sessionId]) {
      setCurrentSessionId(sessionId);
      updateSessionId(sessionId);
      setError(null);
    }
  }, [sessions, updateSessionId]);

  const hydrateSession = useCallback((payload) => {
    if (!user?.id || !payload?.sessionId) return;
    const mapped = (payload.messages || []).map((message, index) => ({
      id: `${payload.sessionId}-${index}`,
      role: message.role,
      content: message.content,
      timestamp: message.created_at || new Date().toISOString(),
      metadata: {}
    }));
    const session = {
      id: payload.sessionId,
      company_id: payload.companyId,
      messages: mapped,
      createdAt: mapped[0]?.timestamp || new Date().toISOString(),
      messageCount: mapped.length,
      fromBackend: true,
      ...(payload.title && { title: payload.title })
    };
    const stored = getStoredSessions(user.id);
    const updated = { ...stored, [payload.sessionId]: session };
    Object.entries(updated).forEach(([id, existing]) => {
      if (id === payload.sessionId) return;
      if (String(existing.company_id) !== String(payload.companyId)) return;
      if ((existing.messages?.length || 0) > 0 || existing.fromBackend) return;
      delete updated[id];
    });
    storeSessions(updated, user.id);
    setSessions(updated);
    setCurrentSessionId(payload.sessionId);
    storeSessionId(payload.sessionId, user.id);
    setError(null);
  }, [user?.id]);

  const handleMessageClick = useCallback(async (messageId, queryId, stepIndex = 0) => {
    if (!queryId || !activeCompany) return;

    const companyIdForRequest = activeCompany.id;
    const sessionIdForRequest = currentSessionId;

    try {
      setIsLoading(true);
      setError(null);

      const response = await runQuery(queryId, activeCompany.id);

      const isStillCurrent =
        currentContextRef.current.activeCompanyId === companyIdForRequest &&
        currentContextRef.current.currentSessionId === sessionIdForRequest;

      const targetSessionId = isStillCurrent ? currentSessionId : sessionIdForRequest;

      setSessions((prev) => {
        const session = prev[targetSessionId];
        if (!session) return prev;

        const messageIndex = session.messages.findIndex((msg) => msg.id === messageId);
        if (messageIndex === -1) return prev;

        const newMessages = [...session.messages];
        const existingData = newMessages[messageIndex].metadata.data;
        const newData = Array.isArray(existingData)
          ? existingData.map((d, i) => (i === stepIndex ? response.data : d))
          : response.data;

        newMessages[messageIndex] = {
          ...newMessages[messageIndex],
          metadata: {
            ...newMessages[messageIndex].metadata,
            data: newData,
            sessionId: response.session_id
          }
        };

        const updatedSessions = {
          ...prev,
          [targetSessionId]: {
            ...session,
            messages: newMessages
          }
        };
        storeSessions(updatedSessions, user?.id);
        return updatedSessions;
      });

      if (isStillCurrent) {
        setSelectedMessageId(messageId);
      }
    } catch (err) {
      console.error('Failed to run query:', err);
      if (
        currentContextRef.current.activeCompanyId === companyIdForRequest &&
        currentContextRef.current.currentSessionId === sessionIdForRequest
      ) {
        setError(err.message || t('errors.failedToRunQuery'));
        setSelectedMessageId(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentSessionId, activeCompany, user?.id, t]);

  const currentMessages = sessions[currentSessionId]?.messages || [];

  const sessionsArray = Object.values(sessions)
    .filter(session => session.company_id === activeCompany?.id)
    .sort((a, b) =>
      new Date(b.createdAt) - new Date(a.createdAt)
    );

  return {
    currentSessionId,
    sessions: sessionsArray,
    sessionsObject: sessions,
    messages: currentMessages,
    isLoading,
    sessionsLoaded,
    error,
    sendQuestion,
    deleteSession,
    startNewSession,
    switchSession,
    hydrateSession,
    handleMessageClick,
    selectedMessageId
  };
}

/**
 * Provider that holds the single conversation state. Must be rendered inside AuthProvider.
 */
export function ConversationProvider({ children }) {
  const value = useConversationState();
  return React.createElement(ConversationContext.Provider, { value }, children);
}

/**
 * Hook to access the shared conversation state. Use inside ConversationProvider.
 * @returns {Object} Conversation state and methods
 */
export const useConversation = () => {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
};
